import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Send, AlertCircle, Calendar, Users, Bell } from 'lucide-react'
import { announcementApi } from '../services/api'
import api from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'
import { showError } from '../utils/showError'
import {
  PageHeader,
  LoadingSpinner,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormField,
  FormSelect,
  FormTextarea,
  FormCheckbox,
  FormGroup,
  Badge,
  ErrorMessage,
  Modal,
  DetailPageLayout
} from '../components/common'

const CreateAnnouncement = () => {
  const navigate = useNavigate()
  const { announcementId } = useParams()
  const isEditing = !!announcementId

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [buildings, setBuildings] = useState([])
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 2, // Medium priority
    eventDate: '',
    scheduleDate: '',
    isAcknowledgeReq: false,
    announceType: [1], // Email by default
    buildingId: ''
  })
  const [errors, setErrors] = useState({})

  // AI Generate state
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)

  // AI Revise state
  const [showReviseModal, setShowReviseModal] = useState(false)
  const [reviseLoading, setReviseLoading] = useState(false)
  const [reviseError, setReviseError] = useState(null)

  useEffect(() => {
    loadBuildings()
    if (isEditing) {
      loadAnnouncementForEdit()
    }
  }, [isEditing, announcementId])

  const loadBuildings = async () => {
    try {
      const response = await api.get('/Building')
      setBuildings(response.data.items || [])
    } catch (error) {
      console.error('Error loading buildings:', error)
    }
  }

  const loadAnnouncementForEdit = async () => {
    try {
      setLoading(true)
      const response = await announcementApi.get(announcementId)
      const announcement = response.data

      setFormData({
        title: announcement.title || '',
        content: announcement.content || '',
        priority: announcement.priority || 2,
        eventDate: announcement.eventDate ? announcement.eventDate.slice(0, 16) : '',
        scheduleDate: announcement.scheduleDate ? announcement.scheduleDate.slice(0, 16) : '',
        isAcknowledgeReq: announcement.isAcknowledgeReq || false,
        announceType: announcement.announceType || [1],
        buildingId: announcement.buildingId || ''
      })
    } catch (error) {
      console.error('Error loading announcement:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less'
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required'
    } else if (formData.content.length > 5000) {
      newErrors.content = 'Content must be 5000 characters or less'
    }

    if (!formData.buildingId) {
      newErrors.buildingId = 'Building is required'
    }

    if (formData.announceType.length === 0) {
      newErrors.announceType = 'At least one notification method is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleNotificationMethodChange = (method, checked) => {
    setFormData(prev => ({
      ...prev,
      announceType: checked
        ? [...prev.announceType, method]
        : prev.announceType.filter(m => m !== method)
    }))

    if (errors.announceType) {
      setErrors(prev => ({ ...prev, announceType: undefined }))
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setSubmitting(true)

      const submitData = {
        ...formData,
        eventDate: formData.eventDate ? new Date(formData.eventDate).toISOString() : null,
        scheduleDate: formData.scheduleDate ? new Date(formData.scheduleDate).toISOString() : null
      }

      if (isEditing) {
        await announcementApi.update(announcementId, {
          ...submitData,
          announcementId: parseInt(announcementId)
        })
      } else {
        await announcementApi.create(submitData)
      }

      navigate('/announcements')
    } catch (error) {
      console.error('Error saving announcement:', error)
      showError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError('Please enter a description for the AI to generate content')
      return
    }

    setAiLoading(true)
    setAiError(null)

    try {
      const response = await announcementApi.generateAI({ command: aiPrompt })

      if (response.data) {
        if (!response.data.result) {
          setAiError(response.data.content || 'The input was not sufficient to generate a valid announcement. Please provide more details.')
          return
        }
        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          content: response.data.content || prev.content
        }))
        setShowAIModal(false)
        setAiPrompt('')
      }
    } catch (err) {
      setAiError(extractErrorMessage(err))
    } finally {
      setAiLoading(false)
    }
  }

  const handleAIRevise = async () => {
    setReviseLoading(true)
    setReviseError(null)

    try {
      const response = await announcementApi.reviseAI({ content: formData.content })

      if (response.data) {
        if (!response.data.result) {
          setReviseError(response.data.content || 'Failed to revise the announcement.')
          return
        }
        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          content: response.data.content || prev.content
        }))
        setShowReviseModal(false)
      }
    } catch (err) {
      setReviseError(extractErrorMessage(err))
    } finally {
      setReviseLoading(false)
    }
  }

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'High'
      case 2: return 'Medium'
      case 3: return 'Low'
      default: return 'Medium'
    }
  }

  // Priority colors now handled by Badge component variants

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <DetailPageLayout
      title={isEditing ? 'Edit Announcement' : 'Create Announcement'}
      backLink={{ label: "Back to Announcements", href: "/announcements" }}
      actions={[
        <Button
          key="save"
          onClick={handleSubmit}
          disabled={submitting}
          variant="primary"
          icon={<Save className="h-4 w-4" />}
          loading={submitting}
        >
          {submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
        </Button>
      ]}
    >

      <Card>
        <CardHeader
          title="Announcement Details"
          action={
            <div className="flex items-center space-x-2">
              <Button
                variant="primary"
                size="small"
                type="button"
                onClick={() => setShowAIModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white border-0"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Generate
              </Button>
              {formData.content.trim() && (
                <Button
                  variant="primary"
                  size="small"
                  type="button"
                  onClick={() => setShowReviseModal(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white border-0"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  AI Revise
                </Button>
              )}
            </div>
          }
        />
        <CardBody className="space-y-6">
          <FormField
            label="Title"
            name="title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            error={errors.title}
            placeholder="Enter announcement title"
            maxLength={200}
            helpText={`${formData.title.length}/200 characters`}
            required
          />

          <FormTextarea
            label="Content"
            name="content"
            value={formData.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            error={errors.content}
            placeholder="Enter announcement content (supports HTML formatting)"
            rows={8}
            maxLength={5000}
            helpText={`${formData.content.length}/5000 characters`}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormSelect
              label="Building"
              name="buildingId"
              value={formData.buildingId}
              onChange={(e) => handleInputChange('buildingId', e.target.value)}
              error={errors.buildingId}
              options={[
                { value: '', label: 'Select a building' },
                ...buildings.map(building => ({
                  value: building.buildingId,
                  label: building.name
                }))
              ]}
              required
            />

            <FormSelect
              label="Priority"
              name="priority"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', parseInt(e.target.value))}
              options={[
                { value: 1, label: 'High Priority' },
                { value: 2, label: 'Medium Priority' },
                { value: 3, label: 'Low Priority' }
              ]}
              helpText={`Current: ${getPriorityText(formData.priority)} Priority`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Event Date (Optional)"
              name="eventDate"
              type="datetime-local"
              value={formData.eventDate}
              onChange={(e) => handleInputChange('eventDate', e.target.value)}
              helpText="When the announced event will occur"
            />

            <FormField
              label="Schedule Date (Optional)"
              name="scheduleDate"
              type="datetime-local"
              value={formData.scheduleDate}
              onChange={(e) => handleInputChange('scheduleDate', e.target.value)}
              helpText="When to send the announcement"
            />
          </div>

          <FormGroup
            title="Notification Methods"
            description="Select how recipients will be notified"
          >
            <div className="space-y-2">
              <FormCheckbox
                label={(
                  <span className="flex items-center">
                    <Bell className="w-4 h-4 mr-1" />
                    Email
                  </span>
                )}
                name="email"
                checked={formData.announceType.includes(1)}
                onChange={(e) => handleNotificationMethodChange(1, e.target.checked)}
              />
              <FormCheckbox
                label={(
                  <span className="flex items-center">
                    <Bell className="w-4 h-4 mr-1" />
                    SMS
                  </span>
                )}
                name="sms"
                checked={formData.announceType.includes(2)}
                onChange={(e) => handleNotificationMethodChange(2, e.target.checked)}
              />
              <FormCheckbox
                label={(
                  <span className="flex items-center">
                    <Bell className="w-4 h-4 mr-1" />
                    Push Notification
                  </span>
                )}
                name="push"
                checked={formData.announceType.includes(3)}
                onChange={(e) => handleNotificationMethodChange(3, e.target.checked)}
              />
            </div>
            {errors.announceType && (
              <ErrorMessage message={errors.announceType} />
            )}
          </FormGroup>

          <FormGroup title="Additional Options">
            <div className="space-y-3">
              <FormCheckbox
                label="Require acknowledgment from recipients"
                name="isAcknowledgeReq"
                checked={formData.isAcknowledgeReq}
                onChange={(e) => handleInputChange('isAcknowledgeReq', e.target.checked)}
              />

              <FormCheckbox
                label="Save as template for future use"
                name="isTemplate"
                checked={formData.isTemplate}
                onChange={(e) => handleInputChange('isTemplate', e.target.checked)}
              />
            </div>
          </FormGroup>
        </CardBody>
      </Card>

      {/* Preview Section */}
      <Card>
        <CardHeader title="Preview" />
        <CardBody>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-medium text-gray-900">
                {formData.title || 'Untitled Announcement'}
              </h4>
              <div className="flex items-center space-x-2">
                <Badge
                  variant={formData.priority === 1 ? 'danger' :
                           formData.priority === 2 ? 'warning' : 'default'}
                >
                  {getPriorityText(formData.priority)} Priority
                </Badge>
                {formData.isAcknowledgeReq && (
                  <Badge variant="primary" className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    Acknowledgment Required
                  </Badge>
                )}
              </div>
            </div>
            {formData.content ? (
              <div
                className="text-gray-700 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: formData.content }}
              />
            ) : (
              <div className="text-gray-700">No content provided</div>
            )}
            {formData.eventDate && (
              <div className="mt-3 flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-1" />
                Event: {new Date(formData.eventDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* AI Generate Modal */}
      <Modal
        isOpen={showAIModal}
        onClose={() => { setShowAIModal(false); setAiError(null) }}
        title="Generate Announcement with AI"
        size="large"
      >
        <div className="space-y-4">
          {aiError && (
            <ErrorMessage
              message={aiError}
              onDismiss={() => setAiError(null)}
            />
          )}

          <FormTextarea
            label="Describe the announcement you want to create"
            name="aiPrompt"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            rows={4}
            placeholder="E.g., Water shutdown on floor 3 tomorrow from 9am to 5pm for pipe maintenance..."
            disabled={aiLoading}
          />

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              The AI will generate a professional announcement with a title and HTML-formatted content based on your description.
            </p>
            {(formData.title || formData.content) && (
              <p className="text-xs text-blue-600 mt-1">
                Existing title and content will be replaced with the AI-generated version.
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowAIModal(false)
                setAiPrompt('')
                setAiError(null)
              }}
              disabled={aiLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleAIGenerate}
              disabled={aiLoading || !aiPrompt.trim()}
              loading={aiLoading}
            >
              Generate
            </Button>
          </div>
        </div>
      </Modal>

      {/* AI Revise Modal */}
      <Modal
        isOpen={showReviseModal}
        onClose={() => { setShowReviseModal(false); setReviseError(null) }}
        title="Revise Announcement with AI"
        size="large"
      >
        <div className="space-y-4">
          {reviseError && (
            <ErrorMessage
              message={reviseError}
              onDismiss={() => setReviseError(null)}
            />
          )}

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              The AI will take your current content and reformat it into a professional, well-structured announcement with HTML formatting, without changing its core message.
            </p>
          </div>

          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
            <p className="text-xs text-gray-500 mb-1 font-medium">Current content:</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{formData.content}</p>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowReviseModal(false)
                setReviseError(null)
              }}
              disabled={reviseLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleAIRevise}
              disabled={reviseLoading}
              loading={reviseLoading}
            >
              Revise
            </Button>
          </div>
        </div>
      </Modal>
    </DetailPageLayout>
  )
}

export default CreateAnnouncement
